/**
 * Guest Fridge UI — In-room cart + dual-rail checkout (Stripe fiat / BHERMES on-chain).
 * Route: /fridge/:hotelId/:roomNumber
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Wine, Coffee, Cookie, Droplets, Zap,
  CreditCard, Wallet, Check, Star, Plus, Minus, ShoppingCart, X, Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BACKEND_URL } from '@/lib/backendUrl';

const CATEGORY_CONFIG = {
  snack:    { icon: Cookie,  color: 'text-amber-400',  bg: 'bg-amber-500/10',  label: 'Snacks' },
  beverage: { icon: Droplets, color: 'text-blue-400',  bg: 'bg-blue-500/10',   label: 'Drinks' },
  premium:  { icon: Star,    color: 'text-purple-400', bg: 'bg-purple-500/10', label: 'Premium' },
  other:    { icon: Coffee,  color: 'text-[#64748b]',  bg: 'bg-white/5',       label: 'Other' },
};

function ProductCard({ product, qty, onInc, onDec }) {
  const cat = CATEGORY_CONFIG[product.category] || CATEGORY_CONFIG.other;
  const CatIcon = cat.icon;
  const savings = (product.price_fiat - product.price_hermes).toFixed(2);
  const inCart = qty > 0;

  return (
    <div
      className={cn(
        'bg-[#0d1220] border rounded-2xl p-4 transition-all',
        product.in_stock ? 'border-[#1a2235] hover:border-[#2a3245]' : 'border-red-500/20 opacity-50',
        inCart && 'border-cyan-500/50',
      )}
      data-testid={`product-${product.product_id}`}
    >
      <div className="flex items-start gap-3">
        <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center shrink-0', cat.bg)}>
          <CatIcon className={cn('w-6 h-6', cat.color)} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">{product.product_name}</p>
          <Badge className={cn('text-[8px] mt-1', cat.bg, cat.color)}>{cat.label}</Badge>
        </div>
        {!product.in_stock && (
          <Badge className="text-[8px] bg-red-500/10 text-red-400 shrink-0">Out of Stock</Badge>
        )}
      </div>

      {/* Pricing */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="bg-[#080c16] rounded-xl p-2.5 text-center border border-[#1a2235]">
          <div className="flex items-center justify-center gap-1 mb-1">
            <CreditCard className="w-3 h-3 text-[#64748b]" />
            <span className="text-[8px] text-[#64748b] uppercase">Card</span>
          </div>
          <p className="text-lg font-bold font-mono text-[#94a3b8]">${product.price_fiat.toFixed(2)}</p>
        </div>

        <div className="bg-gradient-to-br from-cyan-500/5 to-purple-500/5 rounded-xl p-2.5 text-center border border-cyan-500/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 bg-cyan-500 text-[6px] text-white font-bold px-1.5 py-0.5 rounded-bl-lg">
            SAVE {product.savings_pct}%
          </div>
          <div className="flex items-center justify-center gap-1 mb-1">
            <Zap className="w-3 h-3 text-cyan-400" />
            <span className="text-[8px] text-cyan-400 uppercase font-semibold">BHERMES</span>
          </div>
          <p className="text-lg font-bold font-mono text-cyan-400">{product.price_hermes.toFixed(2)}</p>
          <p className="text-[7px] text-emerald-400">Save ${savings}</p>
        </div>
      </div>

      {/* Cart controls */}
      {product.in_stock && (
        <div className="mt-3 flex items-center justify-between gap-2">
          {inCart ? (
            <div className="flex items-center gap-3 flex-1">
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8 border-[#2a3245] bg-[#080c16]"
                onClick={onDec}
                data-testid={`dec-${product.product_id}`}
              >
                <Minus className="w-3 h-3" />
              </Button>
              <span className="text-sm font-mono font-bold flex-1 text-center" data-testid={`qty-${product.product_id}`}>
                {qty}
              </span>
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8 border-cyan-500/40 bg-cyan-500/10 text-cyan-400"
                onClick={onInc}
                data-testid={`inc-${product.product_id}`}
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-[#080c16] font-semibold text-xs h-9"
              onClick={onInc}
              data-testid={`add-${product.product_id}`}
            >
              <Plus className="w-3 h-3 mr-1" /> Add to Cart
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function CartDrawer({ open, onClose, cart, products, onInc, onDec, onCheckout, submitting }) {
  const lines = Object.entries(cart).map(([pid, qty]) => ({
    ...products.find((p) => p.product_id === pid),
    qty,
  }));
  const totalFiat = lines.reduce((s, l) => s + l.price_fiat * l.qty, 0);
  const totalHermes = lines.reduce((s, l) => s + l.price_hermes * l.qty, 0);
  const savings = totalFiat - totalHermes;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-[#0d1220] border-t sm:border border-[#1a2235] rounded-t-2xl sm:rounded-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-[#1a2235]">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-cyan-400" />
            <h3 className="font-semibold" data-testid="cart-title">Your Cart</h3>
            <Badge className="bg-cyan-500/10 text-cyan-400 text-[9px]">{lines.length} items</Badge>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} data-testid="cart-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {lines.length === 0 && (
            <p className="text-center text-[#64748b] text-xs py-8">Cart is empty</p>
          )}
          {lines.map((l) => (
            <div key={l.product_id} className="bg-[#080c16] border border-[#1a2235] rounded-xl p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate">{l.product_name}</p>
                <p className="text-[10px] text-[#64748b] font-mono">
                  ${l.price_fiat.toFixed(2)} · {l.price_hermes.toFixed(2)} BHRM
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="icon" variant="outline" className="h-7 w-7 border-[#2a3245]" onClick={() => onDec(l.product_id)}>
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="text-xs font-mono w-5 text-center">{l.qty}</span>
                <Button size="icon" variant="outline" className="h-7 w-7 border-cyan-500/40 text-cyan-400" onClick={() => onInc(l.product_id)}>
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {lines.length > 0 && (
          <div className="border-t border-[#1a2235] p-4 space-y-3">
            <div className="flex justify-between text-xs">
              <span className="text-[#64748b]">Card total</span>
              <span className="font-mono font-semibold">${totalFiat.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-cyan-400">BHERMES total</span>
              <span className="font-mono font-semibold text-cyan-400">{totalHermes.toFixed(2)} BHRM</span>
            </div>
            <div className="flex justify-between text-[10px] pt-2 border-t border-[#1a2235]">
              <span className="text-emerald-400">You save with BHERMES</span>
              <span className="font-mono text-emerald-400">${savings.toFixed(2)}</span>
            </div>

            <div className="grid grid-cols-1 gap-2 pt-2">
              <Button
                disabled={submitting}
                onClick={() => onCheckout('stripe')}
                className="bg-[#1a2235] hover:bg-[#2a3245] border border-[#2a3245] h-11 text-xs font-semibold"
                data-testid="checkout-stripe-btn"
              >
                {submitting === 'stripe' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay ${totalFiat.toFixed(2)} with Card
                  </>
                )}
              </Button>
              <Button
                disabled={submitting}
                onClick={() => onCheckout('bhermes')}
                className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-[#080c16] h-11 text-xs font-bold"
                data-testid="checkout-bhermes-btn"
              >
                {submitting === 'bhermes' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Pay {totalHermes.toFixed(2)} $BHERMES (−25%)
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BHermesIntentModal({ intent, onConfirm, onClose, submitting }) {
  const [tx, setTx] = useState('');
  const [wallet, setWallet] = useState('');
  if (!intent) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="bhermes-intent-modal">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0d1220] border border-cyan-500/30 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="w-5 h-5 text-cyan-400" />
          <h3 className="font-bold text-sm">Settle On-Chain</h3>
          <Badge className="bg-cyan-500/10 text-cyan-400 text-[9px] ml-auto">Base Sepolia</Badge>
        </div>

        <div className="space-y-3">
          <div className="bg-[#080c16] border border-[#1a2235] rounded-xl p-3">
            <p className="text-[9px] text-[#64748b] uppercase mb-1">Send exactly</p>
            <p className="text-2xl font-bold font-mono text-cyan-400">{intent.amount_bhermes} BHRM</p>
            <p className="text-[10px] text-emerald-400 mt-1">Saved ${intent.savings_vs_fiat_usd} vs card</p>
          </div>

          <div className="bg-[#080c16] border border-[#1a2235] rounded-xl p-3 space-y-2">
            <div>
              <p className="text-[9px] text-[#64748b] uppercase">To contract</p>
              <p className="text-[10px] font-mono break-all text-[#94a3b8]">{intent.settlement_contract}</p>
            </div>
            <div>
              <p className="text-[9px] text-[#64748b] uppercase">Memo</p>
              <p className="text-[10px] font-mono break-all text-cyan-400">{intent.memo}</p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] text-[#94a3b8] uppercase">Your wallet address</label>
            <input
              value={wallet}
              onChange={(e) => setWallet(e.target.value)}
              placeholder="0x..."
              className="w-full bg-[#080c16] border border-[#1a2235] rounded-lg px-3 py-2 text-xs font-mono"
              data-testid="bhermes-wallet-input"
            />
            <label className="text-[10px] text-[#94a3b8] uppercase">Transaction hash</label>
            <input
              value={tx}
              onChange={(e) => setTx(e.target.value)}
              placeholder="0x..."
              className="w-full bg-[#080c16] border border-[#1a2235] rounded-lg px-3 py-2 text-xs font-mono"
              data-testid="bhermes-tx-input"
            />
          </div>

          <Button
            disabled={!tx || !wallet || submitting}
            onClick={() => onConfirm({ tx_hash: tx.trim(), wallet_address: wallet.trim() })}
            className="w-full bg-cyan-500 hover:bg-cyan-400 text-[#080c16] font-bold h-10 text-xs"
            data-testid="bhermes-confirm-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" />Confirm Payment</>}
          </Button>

          <p className="text-[9px] text-[#64748b] text-center">
            Use your Reown embedded wallet or any Base-compatible wallet.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function GuestFridgePage() {
  const { hotelId, roomNumber } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({}); // {product_id: qty}
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(null); // 'stripe' | 'bhermes' | null
  const [intent, setIntent] = useState(null); // BHERMES intent
  const [banner, setBanner] = useState(null);

  const hid = hotelId || 'hotel_grand_marina';
  const room = roomNumber || '305';

  const fetchPricing = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/fridge/pricing/${hid}/${room}`);
      const data = await res.json();
      setPricing(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [hid, room]);

  useEffect(() => { fetchPricing(); }, [fetchPricing]);

  // Handle post-checkout redirect from Stripe
  useEffect(() => {
    const p = searchParams.get('payment');
    const sid = searchParams.get('session_id');
    if (p === 'success' && sid) {
      setBanner({ type: 'success', text: 'Payment processing — verifying with Stripe...' });
      fetch(`${BACKEND_URL}/api/fridge/checkout/stripe/status/${sid}`)
        .then((r) => r.json())
        .then((data) => {
          if (data?.payment_status === 'paid') {
            setBanner({ type: 'success', text: `Paid $${data.amount_total?.toFixed(2) || ''}. Enjoy!` });
            setCart({});
          } else {
            setBanner({ type: 'warn', text: `Payment ${data?.payment_status || 'pending'}.` });
          }
          searchParams.delete('payment');
          searchParams.delete('session_id');
          searchParams.delete('charge_id');
          setSearchParams(searchParams);
        })
        .catch(() => setBanner({ type: 'error', text: 'Could not verify payment.' }));
    } else if (p === 'cancelled') {
      setBanner({ type: 'warn', text: 'Payment cancelled.' });
      searchParams.delete('payment'); searchParams.delete('charge_id');
      setSearchParams(searchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inc = (pid) => setCart((c) => ({ ...c, [pid]: (c[pid] || 0) + 1 }));
  const dec = (pid) => setCart((c) => {
    const q = (c[pid] || 0) - 1;
    if (q <= 0) { const n = { ...c }; delete n[pid]; return n; }
    return { ...c, [pid]: q };
  });

  const cartCount = useMemo(() => Object.values(cart).reduce((s, n) => s + n, 0), [cart]);

  const items = Object.entries(cart).map(([product_id, qty]) => ({ product_id, qty }));

  const checkout = async (method) => {
    if (items.length === 0) return;
    setSubmitting(method);
    try {
      const endpoint = method === 'stripe'
        ? `${BACKEND_URL}/api/fridge/checkout/stripe`
        : `${BACKEND_URL}/api/fridge/checkout/bhermes/intent`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hid,
          room_number: room,
          items,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setBanner({ type: 'error', text: data.detail || 'Checkout failed.' });
        return;
      }
      if (method === 'stripe') {
        window.location.href = data.checkout_url;
      } else {
        setIntent(data);
        setDrawerOpen(false);
      }
    } catch (e) {
      setBanner({ type: 'error', text: 'Checkout failed. Try again.' });
    } finally {
      setSubmitting(null);
    }
  };

  const confirmBHermes = async ({ tx_hash, wallet_address }) => {
    setSubmitting('bhermes');
    try {
      const res = await fetch(`${BACKEND_URL}/api/fridge/checkout/bhermes/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ charge_id: intent.charge_id, tx_hash, wallet_address }),
      });
      const data = await res.json();
      if (data.success) {
        setBanner({ type: 'success', text: data.message || 'Settled on-chain!' });
        setIntent(null);
        setCart({});
      } else {
        setBanner({ type: 'error', text: data.detail || 'Confirmation failed.' });
      }
    } finally {
      setSubmitting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080c16] flex items-center justify-center">
        <div className="text-[#64748b] text-sm font-mono">Loading fridge...</div>
      </div>
    );
  }

  if (!pricing || !pricing.products) {
    return (
      <div className="min-h-screen bg-[#080c16] flex items-center justify-center p-4">
        <div className="text-center">
          <Wine className="w-12 h-12 text-[#1a2235] mx-auto mb-3" />
          <p className="text-[#64748b] text-sm">No smart fridge in Room {room}</p>
        </div>
      </div>
    );
  }

  const products = pricing.products;
  const categories = [...new Set(products.map((p) => p.category))];
  const inStock = products.filter((p) => p.in_stock);

  return (
    <div className="min-h-screen bg-[#080c16] text-[#f1f5f9] pb-24" data-testid="guest-fridge-page">
      {/* Banner */}
      {banner && (
        <div
          className={cn(
            'fixed top-3 left-1/2 -translate-x-1/2 z-[70] px-4 py-2 rounded-full text-[11px] font-semibold border shadow-xl',
            banner.type === 'success' && 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',
            banner.type === 'warn'    && 'bg-amber-500/15 border-amber-500/40 text-amber-300',
            banner.type === 'error'   && 'bg-red-500/15 border-red-500/40 text-red-300',
          )}
          data-testid="fridge-banner"
        >
          {banner.text}
          <button className="ml-3 opacity-60 hover:opacity-100" onClick={() => setBanner(null)}>×</button>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-b from-[#0d1220] to-[#080c16] px-4 pt-6 pb-4">
        <div className="max-w-md mx-auto text-center">
          <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mx-auto mb-3">
            <Wine className="w-7 h-7 text-cyan-400" />
          </div>
          <h1 className="text-xl font-bold" data-testid="fridge-title">Room {room} Mini Bar</h1>
          <p className="text-[11px] text-[#64748b] mt-1">Tap + to add, then checkout with card or $BHERMES</p>

          <div className="mt-4 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-3">
            <div className="flex items-center justify-center gap-2">
              <Zap className="w-4 h-4 text-cyan-400" />
              <p className="text-xs font-semibold text-cyan-400">Pay with $BHERMES, save 25%</p>
            </div>
            <p className="text-[9px] text-[#94a3b8] mt-1">
              {inStock.length} items in stock · Card payments incl. {pricing.fiat_fee} processing fee
            </p>
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="max-w-md mx-auto px-4 pb-8 space-y-6">
        {categories.map((cat) => {
          const catConfig = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.other;
          const catProducts = products.filter((p) => p.category === cat);
          return (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-3">
                <catConfig.icon className={cn('w-4 h-4', catConfig.color)} />
                <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8]">{catConfig.label}</h2>
                <Badge className="text-[7px] bg-white/5 border-white/10">{catProducts.length}</Badge>
              </div>
              <div className="space-y-2">
                {catProducts.map((p) => (
                  <ProductCard
                    key={p.product_id}
                    product={p}
                    qty={cart[p.product_id] || 0}
                    onInc={() => inc(p.product_id)}
                    onDec={() => dec(p.product_id)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Sticky Cart Button */}
      {cartCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 p-4 bg-gradient-to-t from-[#080c16] via-[#080c16] to-transparent">
          <Button
            onClick={() => setDrawerOpen(true)}
            className="max-w-md mx-auto w-full h-12 bg-cyan-500 hover:bg-cyan-400 text-[#080c16] font-bold shadow-xl shadow-cyan-500/20 flex items-center justify-between px-5"
            data-testid="open-cart-btn"
          >
            <span className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" />
              Checkout
            </span>
            <Badge className="bg-[#080c16] text-cyan-400 border-0" data-testid="cart-count">{cartCount}</Badge>
          </Button>
        </div>
      )}

      <CartDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        cart={cart}
        products={products}
        onInc={inc}
        onDec={dec}
        onCheckout={checkout}
        submitting={submitting}
      />

      <BHermesIntentModal
        intent={intent}
        onConfirm={confirmBHermes}
        onClose={() => setIntent(null)}
        submitting={submitting === 'bhermes'}
      />
    </div>
  );
}
